import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';
import { peopleProfiles, linkedInPosts } from '../utils/mockData';
import { TrendingUpIcon, BarChart2Icon, PieChartIcon, InfoIcon, DownloadIcon } from 'lucide-react';
export const IntentSignalAnalytics: React.FC = () => {
  const [chartType, setChartType] = useState<'intent' | 'engagement' | 'conversion'>('intent');
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('week');
  // Calculate intent signals by type
  const intentSignalsByType = [{
    name: 'High Intent',
    value: peopleProfiles.filter(p => p.relevance === 'high').length
  }, {
    name: 'Medium Intent',
    value: peopleProfiles.filter(p => p.relevance === 'medium').length
  }, {
    name: 'Low Intent',
    value: peopleProfiles.filter(p => p.relevance === 'low').length
  }];
  // Calculate engagement by type
  const engagementTypes = peopleProfiles.reduce((acc, person) => {
    person.interactions.forEach(interaction => {
      if (interaction.type === 'liked') acc.likes++;else if (interaction.type === 'commented') acc.comments++;else if (interaction.type === 'shared') acc.shares++;
    });
    return acc;
  }, {
    likes: 0,
    comments: 0,
    shares: 0
  });
  const engagementByType = [{
    name: 'Likes',
    value: engagementTypes.likes
  }, {
    name: 'Comments',
    value: engagementTypes.comments
  }, {
    name: 'Shares',
    value: engagementTypes.shares
  }];
  // Calculate conversion funnel
  const conversionFunnel = [{
    name: 'Viewed',
    value: 120
  }, {
    name: 'Engaged',
    value: peopleProfiles.length
  }, {
    name: 'Responded',
    value: Math.round(peopleProfiles.length * 0.4)
  }, {
    name: 'Meeting',
    value: Math.round(peopleProfiles.length * 0.2)
  }, {
    name: 'Deal',
    value: Math.round(peopleProfiles.length * 0.05)
  }];
  // Calculate approach effectiveness
  const approachEffectiveness = [{
    name: 'Comment Follow-up',
    effectiveness: 82
  }, {
    name: 'Shared Interest',
    effectiveness: 76
  }, {
    name: 'Value-First',
    effectiveness: 68
  }, {
    name: 'Direct Question',
    effectiveness: 45
  }, {
    name: 'Generic',
    effectiveness: 23
  }];
  // Intent signals over time
  const intentOverTime = [{
    date: 'Mon',
    high: 2,
    medium: 3,
    low: 4
  }, {
    date: 'Tue',
    high: 3,
    medium: 4,
    low: 5
  }, {
    date: 'Wed',
    high: 4,
    medium: 6,
    low: 4
  }, {
    date: 'Thu',
    high: 3,
    medium: 5,
    low: 3
  }, {
    date: 'Fri',
    high: 5,
    medium: 7,
    low: 2
  }, {
    date: 'Sat',
    high: 4,
    medium: 5,
    low: 3
  }, {
    date: 'Sun',
    high: 3,
    medium: 4,
    low: 2
  }];
  // Colors for the pie chart
  const COLORS = ['#10b981', '#f9b30f', '#6b7280', '#8b5cf6', '#ec4899'];
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" data-id="element-334">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-335">
        <h2 className="text-xl font-medium text-gray-900" data-id="element-336">
          Intent Signal Analytics
        </h2>
        <div className="flex flex-wrap gap-3" data-id="element-337">
          <div className="flex space-x-2" data-id="element-338">
            <button onClick={() => setChartType('intent')} className={`px-3 py-1 text-sm rounded-md ${chartType === 'intent' ? 'bg-[#f9b30f] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-339">
              <span className="hidden sm:inline" data-id="element-340">Intent Signals</span>
              <span className="sm:hidden" data-id="element-341">Intent</span>
            </button>
            <button onClick={() => setChartType('engagement')} className={`px-3 py-1 text-sm rounded-md ${chartType === 'engagement' ? 'bg-[#f9b30f] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-342">
              <span className="hidden sm:inline" data-id="element-343">Engagement Types</span>
              <span className="sm:hidden" data-id="element-344">Engage</span>
            </button>
            <button onClick={() => setChartType('conversion')} className={`px-3 py-1 text-sm rounded-md ${chartType === 'conversion' ? 'bg-[#f9b30f] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-345">
              <span className="hidden sm:inline" data-id="element-346">Conversion Funnel</span>
              <span className="sm:hidden" data-id="element-347">Funnel</span>
            </button>
          </div>
          <div data-id="element-348">
            <select className="pl-3 pr-10 py-1 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" value={timeRange} onChange={e => setTimeRange(e.target.value as any)} data-id="element-349">
              <option value="week" data-id="element-350">Last 7 days</option>
              <option value="month" data-id="element-351">Last 30 days</option>
              <option value="quarter" data-id="element-352">Last 90 days</option>
            </select>
          </div>
          <button className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-353">
            <DownloadIcon size={14} className="mr-1" data-id="element-354" />
            Export
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-355">
        {/* Left side chart */}
        <div data-id="element-356">
          <h3 className="text-md font-medium text-gray-700 mb-2 flex items-center" data-id="element-357">
            {chartType === 'intent' && 'Intent Signal Distribution'}
            {chartType === 'engagement' && 'Engagement by Type'}
            {chartType === 'conversion' && 'Conversion Funnel'}
            <span className="ml-2 text-gray-400 cursor-help" title="This chart shows distribution of intent signals detected from LinkedIn interactions" data-id="element-358">
              <InfoIcon size={16} data-id="element-359" />
            </span>
          </h3>
          <div className="h-64 sm:h-80" data-id="element-360">
            <ResponsiveContainer width="100%" height="100%" data-id="element-361">
              {chartType === 'conversion' ? <BarChart data={conversionFunnel} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-362">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-363" />
                  <XAxis dataKey="name" data-id="element-364" />
                  <YAxis data-id="element-365" />
                  <Tooltip data-id="element-366" />
                  <Bar dataKey="value" fill="#f9b30f" name="Count" data-id="element-367" />
                </BarChart> : chartType === 'intent' ? <div className="h-full" data-id="element-368">
                  <div className="h-1/2 pb-4" data-id="element-369">
                    <PieChart data-id="element-370">
                      <Pie data={intentSignalsByType} cx="50%" cy="50%" labelLine={false} outerRadius={70} fill="#8884d8" dataKey="value" label={({
                    name,
                    percent
                  }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-371">
                        {intentSignalsByType.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-372" />)}
                      </Pie>
                      <Legend data-id="element-373" />
                      <Tooltip formatter={value => [`${value} people`, 'Count']} data-id="element-374" />
                    </PieChart>
                  </div>
                  <div className="h-1/2 pt-2" data-id="element-375">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-376">
                      <LineChart data={intentOverTime} margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5
                  }} data-id="element-377">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-378" />
                        <XAxis dataKey="date" data-id="element-379" />
                        <YAxis data-id="element-380" />
                        <Tooltip data-id="element-381" />
                        <Line type="monotone" dataKey="high" stroke="#10b981" name="High Intent" data-id="element-382" />
                        <Line type="monotone" dataKey="medium" stroke="#f9b30f" name="Medium Intent" data-id="element-383" />
                        <Line type="monotone" dataKey="low" stroke="#6b7280" name="Low Intent" data-id="element-384" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div> : <PieChart data-id="element-385">
                  <Pie data={engagementByType} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                name,
                percent
              }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-386">
                    {engagementByType.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-387" />)}
                  </Pie>
                  <Legend data-id="element-388" />
                  <Tooltip formatter={value => [`${value} items`, 'Count']} data-id="element-389" />
                </PieChart>}
            </ResponsiveContainer>
          </div>
        </div>
        {/* Right side chart */}
        <div data-id="element-390">
          <h3 className="text-md font-medium text-gray-700 mb-2 flex items-center" data-id="element-391">
            Approach Effectiveness
            <span className="ml-2 text-gray-400 cursor-help" title="This chart shows which outreach approaches have the highest response rates" data-id="element-392">
              <InfoIcon size={16} data-id="element-393" />
            </span>
          </h3>
          <div className="h-64 sm:h-80" data-id="element-394">
            <ResponsiveContainer width="100%" height="100%" data-id="element-395">
              <BarChart data={approachEffectiveness} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} layout="vertical" data-id="element-396">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} data-id="element-397" />
                <XAxis type="number" domain={[0, 100]} data-id="element-398" />
                <YAxis dataKey="name" type="category" width={100} data-id="element-399" />
                <Tooltip formatter={value => [`${value}%`, 'Response Rate']} data-id="element-400" />
                <Bar dataKey="effectiveness" fill="#4f46e5" name="Response Rate" data-id="element-401" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-402">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-403">
          <div data-id="element-404">
            <h3 className="text-sm font-medium text-gray-900" data-id="element-405">Key Insights</h3>
            <p className="text-sm text-gray-600 mt-1" data-id="element-406">
              Comment follow-ups and shared interest approaches generate
              significantly higher response rates than generic outreach
              messages.
              {chartType === 'intent' && ' High intent signals have increased by 23% this week.'}
              {chartType === 'conversion' && ' The drop-off between engagement and response stages suggests improving initial message quality.'}
            </p>
          </div>
          <div className="text-right" data-id="element-407">
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-408">
              <BarChart2Icon size={14} className="mr-1" data-id="element-409" />
              Detailed Reports
            </button>
          </div>
        </div>
      </div>
    </div>;
};